import { _numberEnumValues } from '@naturalcycles/js-lib'
import { Experiment, getBucket, WebSignupQueryParam } from '@naturalcycles/shared'
import { InsuranceProviderOption } from '@src/app/shared/typings/enum/insurance-provider.js'
import { ClearbluePage, Page, ParentPage } from '@src/app/shared/typings/enum/pages.js'
import { PageFlowDefinition } from '../core/services/flow.service.js'
import { flowUtils } from '../core/util/flow.util.js'
import { PagePath } from '../core/util/pagePath.js'

export const lastQuizPagePath = new PagePath(ParentPage.quiz, Page.lastQuizPage)

export enum FlowDefinitionName {
  SIGNUP = 'signup',
  CLEAR_BLUE = 'clearblue',
}

export const cloneFlowDefinition = (flow: PageFlowDefinition): PageFlowDefinition => {
  return {
    conditions: {
      ...flow.conditions,
    },
    members: {
      ...flow.members,
    },
    name: flow.name,
    initialFlow: [...flow.initialFlow],
  }
}

export const signupFlowDefinition: PageFlowDefinition = {
  name: FlowDefinitionName.SIGNUP,
  members: {
    [lastQuizPagePath.toString()]: {
      isComplete: ({ state, params }) =>
        flowUtils.hasCompletedQuiz({ state, params }) ||
        (flowUtils.isOuraFlow({ state, params }) &&
          getBucket(
            state.experiment.assignments[Experiment.QUIZ_OURA_FLOW_FIRST_SCREEN_ITERATION],
          ) !== 'test') ||
        flowUtils.isEmailVerificationFlow({ state, params }), // AB 251,
    },
    [Page.measuringDevice]: {
      isComplete: args => flowUtils.hasMeasuringDevice(args),
    },
    [Page.goal]: {
      isComplete: args => flowUtils.hasGoal(args),
    },
    [Page.plans]: {
      isComplete: args => flowUtils.hasPlan(args),
    },
    [Page.credentials]: {
      isComplete: args => flowUtils.isLoggedIn(args) && !flowUtils.requiresAppConsent(args),
    },
    [Page.address]: {
      isComplete: args =>
        flowUtils.hasAddress(args) && flowUtils.hasValidThermometerSelection(args),
    },
    [Page.payment]: {
      isComplete: args =>
        !flowUtils.hasCartItems(args) &&
        !flowUtils.hasPaymentDue(args) &&
        flowUtils.hasOrdered(args),
    },
    [Page.orderSummary]: {
      // There's no next page after the order summary (for now) so it's always complete
      isComplete: () => true,
    },
    [Page.coveredByInsurance]: {
      isComplete: ({ state, params }) => {
        const hasDevice = flowUtils.hasMeasuringDevice({ state, params })
        // added with ab288
        const isUserDone = flowUtils.hasSeenCoveredByInsurance({ state, params })
        return hasDevice && isUserDone
      },
    },
    [Page.chooseProvider]: {
      isComplete: args => {
        return flowUtils.hasInsuranceProviderParam(args)
      },
    },
    [Page.providerInfo]: {
      isComplete: args => signupFlowDefinition.members[Page.chooseProvider]!.isComplete(args),
    },
    [Page.clarifyProvider]: {
      isComplete: args => {
        return flowUtils.hasInsuranceProviderVariantParam(args)
      },
    },
  },
  /* We see a pattern where if every branch for the condition of PageA lead to PageB, we don't
  need to return anything past PageB from the condition. This is the case for all conditions here
  except the last one, where we return the whole rest of the flow. */
  conditions: {
    [lastQuizPagePath.toString()]: ({ state, params }) => {
      if (flowUtils.shouldSkipMeasuringDevicePage()) {
        const afterMeasuringDevice = signupFlowDefinition.conditions[Page.measuringDevice]!({
          state,
          params,
        }).filter(p => p !== Page.measuringDevice)
        return [lastQuizPagePath, ...afterMeasuringDevice]
      }
      return [lastQuizPagePath, Page.measuringDevice]
    },

    [Page.measuringDevice]: ({ state, params }) => {
      if (flowUtils.isEmailVerificationFlow({ state, params })) {
        return [Page.plans]
      }

      const pages = [Page.measuringDevice]

      // Only Oura users see the goal page
      if (flowUtils.isOuraFlow({ state, params })) {
        pages.push(Page.goal)
        // For US users, add insurance-related pages
      } else if (state.userLocale.country === 'US') {
        pages.push(Page.coveredByInsurance, Page.chooseProvider, Page.providerInfo)
      }

      pages.push(Page.plans)

      return pages
    },

    // Users with a non-selectable plan skip the plans page
    [Page.plans]: args => {
      if (flowUtils.hasNonSelectablePlan(args)) {
        return [Page.credentials]
      }
      return [Page.plans, Page.credentials]
    },
    // Users who are logged in skip the credentials page
    [Page.credentials]: args => {
      const postCredentialsPages = [Page.address, Page.payment, Page.orderSummary]
      if (flowUtils.isLoggedIn(args) && !flowUtils.requiresAppConsent(args)) {
        return postCredentialsPages
      }
      return [Page.credentials, ...postCredentialsPages]
    },

    [Page.address]: args => {
      if (!flowUtils.requiresAddress(args)) {
        return [Page.payment, Page.orderSummary]
      }
      return [Page.address, Page.payment, Page.orderSummary]
    },

    [Page.chooseProvider]: args => {
      const defaultFlow = [Page.chooseProvider, Page.providerInfo, Page.plans]
      // ipParam is actually guaranteed to exist by isComplete but let's be safe
      if (!flowUtils.hasInsuranceProviderParam(args)) {
        return defaultFlow
      }

      const selectedInsuranceProvider = Number(args.params[WebSignupQueryParam.ip])
      // Users can skip the rest of the insurance flow
      if (selectedInsuranceProvider === InsuranceProviderOption.SKIP) {
        return [Page.chooseProvider, Page.plans]
      }

      const providerRequiringClarification = [
        InsuranceProviderOption.BLUECROSS_BLUESHIELD,
        InsuranceProviderOption.OTHER,
      ]
      // Any provider not requiring the clarification page is a default provider
      const defaultProviders = _numberEnumValues(InsuranceProviderOption).filter(
        v => !providerRequiringClarification.includes(v),
      )
      // If the providers not needing clarification are selected, show the provider info page
      if (defaultProviders.includes(selectedInsuranceProvider)) {
        return defaultFlow
      }

      // If the provider needs clarification, show the clarification page after this one
      return [Page.chooseProvider, Page.clarifyProvider, ...defaultFlow.slice(1)]
    },
  },
  initialFlow: [lastQuizPagePath, Page.measuringDevice, Page.goal],
}

const clearblueMembers = {
  [ParentPage.clearblue]: new PagePath(ParentPage.clearblue),
  [ClearbluePage.code]: new PagePath(ParentPage.clearblue, ClearbluePage.code),
  [ClearbluePage.success]: new PagePath(ParentPage.clearblue, ClearbluePage.success),
  [ClearbluePage.failure]: new PagePath(ParentPage.clearblue, ClearbluePage.failure),
  [ClearbluePage.offer]: new PagePath(ParentPage.clearblue, ClearbluePage.offer),
  [Page.orderSummary]: new PagePath(ParentPage.clearblue, Page.orderSummary),
  [Page.address]: new PagePath(ParentPage.clearblue, Page.address),
  [Page.payment]: new PagePath(ParentPage.clearblue, Page.payment),
  [Page.credentials]: new PagePath(ParentPage.clearblue, Page.credentials),
  [ParentPage.quiz]: new PagePath(ParentPage.quiz, Page.lastQuizPage),
}

const paymentFlowPartial = [
  clearblueMembers.address,
  clearblueMembers.payment,
  clearblueMembers[Page.orderSummary],
]
const nonPaymentFlowPartial = [clearblueMembers[Page.orderSummary]]

export const clearblueFlowDefinition: PageFlowDefinition = {
  name: FlowDefinitionName.CLEAR_BLUE,
  members: {
    [clearblueMembers.code.toString()]: {
      isComplete: ({ state }) => !!state.discountCode,
    },
    [clearblueMembers.success.toString()]: {
      isComplete: () => true,
    },
    [clearblueMembers.failure.toString()]: {
      isComplete: () => true,
    },
    [clearblueMembers.offer.toString()]: {
      isComplete: args => flowUtils.hasDecidedOnCbOffer(args),
    },
    [clearblueMembers['order-summary'].toString()]: {
      isComplete: () => true,
    },
    [clearblueMembers.address.toString()]: {
      isComplete: args => flowUtils.hasAddress(args),
    },
    [clearblueMembers.payment.toString()]: {
      isComplete: args => {
        return (
          !flowUtils.hasCartItems(args) &&
          !flowUtils.hasPaymentDue(args) &&
          flowUtils.hasOrdered(args)
        )
      },
    },
    [clearblueMembers.credentials.toString()]: {
      isComplete: args => flowUtils.isLoggedIn(args) && !flowUtils.requiresAppConsent(args),
    },
    [clearblueMembers[ParentPage.quiz].toString()]: {
      isComplete: args => flowUtils.hasCompletedCbQuiz(args),
    },
  },
  conditions: {
    [clearblueMembers.success.toString()]: args => {
      if (flowUtils.hasCBCode(args)) {
        return [
          clearblueMembers.success,
          clearblueMembers[ParentPage.quiz],
          clearblueMembers.offer,
          clearblueMembers.credentials,
          clearblueMembers.payment,
        ]
      }
      return [clearblueMembers.failure]
    },
    [clearblueMembers.failure.toString()]: args => {
      if (flowUtils.hasCBCode(args)) {
        return [clearblueMembers.success, clearblueMembers.offer, clearblueMembers.credentials]
      }
      return [clearblueMembers.failure]
    },
    [clearblueMembers.offer.toString()]: args => {
      const flow = [clearblueMembers.offer]
      if (!flowUtils.isLoggedIn(args)) {
        flow.push(clearblueMembers.credentials)
        return flow
      }
      flow.push(...paymentFlowPartial)

      return flow
    },
    [clearblueMembers.credentials.toString()]: args => {
      const flow: PagePath[] = []
      if (!flowUtils.isLoggedIn(args)) {
        flow.push(clearblueMembers.credentials)
      }
      flow.push(...paymentFlowPartial)
      return flow
    },
    [clearblueMembers.address.toString()]: args => {
      if (!flowUtils.hasYearlySubscription(args)) {
        return nonPaymentFlowPartial
      }
      return paymentFlowPartial
    },
  },
  initialFlow: [clearblueMembers.code, clearblueMembers.success],
}
